import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileSignature, Upload, Eye } from "lucide-react";
import { ensureDLToken } from "@/lib/dlToken";
import { fmtDate } from "@/lib/datetime";

// Super Admin: upload / replace the HIPAA template emailed to drivers for signature.
export function HipaaTemplateCard() {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const load = () => api.get("/hipaa/template").then((r) => setTpl(r.data?.file_id ? r.data : null)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await api.post("/hipaa/template", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("HIPAA template saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const view = async () => {
    const token = await ensureDLToken();
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/hipaa/template/file?auth=${encodeURIComponent(token)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <Card className="p-3 space-y-2 border-primary/30" data-testid="hipaa-template-card">
      <div className="flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-primary" />
        <div className="text-sm font-semibold flex-1">HIPAA Template (sent to drivers)</div>
      </div>
      {tpl ? (
        <div className="text-[11px] text-muted-foreground" data-testid="hipaa-template-name">
          <span className="font-medium text-foreground">{tpl.filename}</span> · uploaded {fmtDate(tpl.uploaded_at)}
        </div>
      ) : (
        <div className="text-[11px] text-amber-700" data-testid="hipaa-template-missing">No template yet — upload a PDF (or image) to start sending it to drivers.</div>
      )}
      <div className="flex gap-1.5">
        <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="hipaa-template-input" />
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="hipaa-template-upload-btn">
          <Upload className="h-3 w-3 mr-1" /> {tpl ? "Replace" : "Upload"}
        </Button>
        {tpl && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={view} data-testid="hipaa-template-view-btn">
            <Eye className="h-3 w-3 mr-1" /> View
          </Button>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground">Word files: save as PDF first.</p>
    </Card>
  );
}
